<template>
  <div class="review-page">
    <div class="review-card">
      <div v-if="loading" class="review-loading">
        {{ t({ fr: 'Chargement de la soumission…', en: 'Loading submission…' }) }}
      </div>

      <div v-else-if="!review" class="notice-warn">
        {{ t({
          fr: "Soumission introuvable. Le lien est peut-être incomplet ou la soumission n'a pas encore été finalisée.",
          en: 'Submission not found. The link may be incomplete, or the submission is not finalized yet.'
        }) }}
      </div>

      <template v-else>
        <h2>{{ t({ fr: 'Revue de votre soumission', en: 'Review of your submission' }) }}</h2>

        <p class="review-meta">
          <span v-if="review.organisation_name"><strong>{{ review.organisation_name }}</strong> — </span>
          {{ t({ fr: 'soumise le', en: 'submitted on' }) }} {{ formatDate(review.created_at) }}
        </p>

        <!-- Read-only answers -->
        <section class="review-section">
          <h3>{{ t({ fr: 'Vos réponses', en: 'Your answers' }) }}</h3>
          <div class="display-survey">
            <SurveyComponent :model="displaySurvey" :key="currentLocale" />
          </div>
        </section>

        <!-- Files -->
        <section class="review-section">
          <h3>{{ t({ fr: 'Fichiers téléversés', en: 'Uploaded files' }) }}</h3>
          <ul v-if="files.length" class="file-list">
            <li v-for="f in files" :key="f.key">
              <span class="file-name">{{ f.filename }}</span>
              <span class="file-size">{{ formatSize(f.size) }}</span>
            </li>
          </ul>
          <p v-else class="notice-info">
            {{ t({ fr: 'Aucun fichier téléversé pour cette soumission.', en: 'No file uploaded for this submission.' }) }}
          </p>
        </section>

        <!-- Additions history -->
        <section v-if="additions.length" class="review-section">
          <h3>{{ t({ fr: 'Ajouts et commentaires', en: 'Additions and comments' }) }}</h3>
          <ul class="additions-list">
            <li v-for="(a, i) in additions" :key="i">
              <div class="addition-date">{{ formatDate(a.added_at) }}</div>
              <div v-if="a.comment" class="addition-comment">{{ a.comment }}</div>
              <div v-if="a.files && a.files.length" class="addition-files">
                {{ a.files.map(x => x.filename).join(', ') }}
              </div>
            </li>
          </ul>
        </section>

        <!-- Add files / comments to this submission -->
        <section class="review-section add-section">
          <h3>{{ t({ fr: 'Ajouter des fichiers ou un commentaire', en: 'Add files or a comment' }) }}</h3>
          <p>{{ t({
            fr: 'Vous pouvez compléter cette soumission avec des fichiers ou des précisions supplémentaires.',
            en: 'You can complete this submission with extra files or clarifications.'
          }) }}</p>

          <FileUploader :key="'up-' + uploaderKey" :token="token" @update:files="onNewFiles" />

          <label class="comment-label">{{ t({ fr: 'Commentaire (facultatif)', en: 'Comment (optional)' }) }}</label>
          <textarea v-model="comment" class="comment-box" rows="4"></textarea>

          <p v-if="addMessage" class="notice-info">{{ addMessage }}</p>
          <p v-if="addError" class="notice-warn">{{ addError }}</p>

          <button class="btn-primary" :disabled="adding || (newFiles.length === 0 && !comment.trim())" @click="submitAddition">
            {{ adding
              ? t({ fr: 'Ajout en cours…', en: 'Adding…' })
              : t({ fr: 'Ajouter à ma soumission', en: 'Add to my submission' }) }}
          </button>
        </section>

        <!-- Resubmit -->
        <section class="review-section">
          <h3>{{ t({ fr: 'Faire une nouvelle soumission', en: 'Make another submission' }) }}</h3>
          <p>{{ t({
            fr: 'Démarrez une nouvelle soumission préremplie avec vos réponses précédentes (nouveau dossier de fichiers).',
            en: 'Start a new submission pre-filled with your previous answers (new file folder).'
          }) }}</p>
          <router-link :to="{ path: '/survey', query: { from: token } }" class="btn-secondary">
            {{ t({ fr: 'Nouvelle soumission préremplie', en: 'New pre-filled submission' }) }}
          </router-link>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { Model } from 'survey-core'
import { SurveyComponent } from 'survey-vue3-ui'
import 'survey-core/survey-core.css'
import 'survey-core/i18n/french'
import { surveyJson } from '../data/survey-questions.js'
import FileUploader from '../components/FileUploader.vue'
import { t, localeState } from '../i18n/index.js'
import { getSubmissionReview, appendToSubmission } from '../services/api.js'

export default {
  name: 'ReviewPage',
  components: { SurveyComponent, FileUploader },

  data () {
    // markRaw: keep the model out of Vue's reactive proxy (see SurveyPage).
    const displaySurvey = markRaw(new Model(surveyJson))
    displaySurvey.mode = 'display'
    displaySurvey.showNavigationButtons = false
    displaySurvey.showProgressBar = 'off'
    displaySurvey.questionsOnPageMode = 'singlePage'
    displaySurvey.showCompletedPage = false
    displaySurvey.locale = localeState.current
    displaySurvey.onTextMarkdown.add((sender, options) => {
      options.html = options.text
    })
    return {
      displaySurvey,
      loading: true,
      review: null,
      files: [],
      additions: [],
      newFiles: [],
      comment: '',
      adding: false,
      addMessage: '',
      addError: '',
      uploaderKey: 0
    }
  },

  computed: {
    token () {
      return (this.$route.query.token || '').toString()
    },
    currentLocale () {
      return localeState.current
    }
  },

  watch: {
    currentLocale (loc) {
      this.displaySurvey.locale = loc
    }
  },

  mounted () {
    this.load()
  },

  methods: {
    t,

    async load () {
      this.loading = true
      const data = await getSubmissionReview(this.token)
      if (data) {
        this.review = data
        this.files = Array.isArray(data.uploaded_files) ? data.uploaded_files : []
        this.additions = Array.isArray(data.additions) ? data.additions : []
        this.displaySurvey.data = data.json_data || {}
      }
      this.loading = false
    },

    onNewFiles (files) {
      this.newFiles = files
    },

    async submitAddition () {
      this.adding = true
      this.addError = ''
      this.addMessage = ''
      try {
        await appendToSubmission(this.token, this.newFiles, this.comment.trim() || null)
        this.comment = ''
        this.newFiles = []
        this.uploaderKey++ // reset the uploader widget
        await this.load()
        this.addMessage = t({ fr: 'Ajout enregistré. Merci !', en: 'Addition saved. Thank you!' })
      } catch (err) {
        const base = t({ fr: "Échec de l'ajout.", en: 'Failed to add.' })
        this.addError = err && err.message ? `${base} (${err.message})` : base
        console.error(err)
      } finally {
        this.adding = false
      }
    },

    formatSize (bytes) {
      if (bytes == null) return ''
      const units = ['o', 'Ko', 'Mo', 'Go']
      let n = Number(bytes)
      let i = 0
      while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
      return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
    },

    formatDate (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      return d.toLocaleDateString(localeState.current === 'en' ? 'en-CA' : 'fr-CA', {
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.review-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px 20px;
}

.review-card {
  background: var(--aecom-white);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 53, 62, 0.1);
  padding: 30px 24px;
}

.review-card h2 {
  color: var(--aecom-midnight-teal);
  border-bottom: 3px solid var(--aecom-lime-green);
  padding-bottom: 12px;
  margin-top: 0;
}

.review-meta {
  color: var(--aecom-black-75, #646363);
  margin-bottom: 24px;
}

.review-section {
  margin: 28px 0;
  padding-top: 8px;
}

.review-section h3 {
  color: var(--aecom-earth-green);
  border-left: 4px solid var(--aecom-lime-green);
  padding-left: 12px;
  font-size: 1.2rem;
}

.display-survey {
  border: 1px solid var(--aecom-black-15, #e3e3e3);
  border-radius: 6px;
  padding: 8px 12px;
}

.file-list, .additions-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.file-list li {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  border-left: 4px solid var(--aecom-earth-green);
  background: #f0f7f5;
  border-radius: 4px;
  margin-bottom: 8px;
}

.file-name { font-weight: 600; color: var(--aecom-midnight-teal); word-break: break-all; }
.file-size { color: var(--aecom-black-75, #646363); white-space: nowrap; }

.additions-list li {
  padding: 10px 14px;
  background: #f8f9fa;
  border-left: 4px solid var(--aecom-lime-green);
  border-radius: 4px;
  margin-bottom: 8px;
}
.addition-date { font-size: 0.8rem; color: var(--aecom-black-75, #646363); }
.addition-comment { margin: 4px 0; color: var(--aecom-midnight-teal); }
.addition-files { font-size: 0.85rem; color: var(--aecom-earth-green); }

.add-section {
  background: #fafcfa;
  border: 1px solid var(--aecom-black-15, #e3e3e3);
  border-radius: 8px;
  padding: 16px 18px;
}

.comment-label {
  display: block;
  font-weight: 600;
  color: var(--aecom-midnight-teal);
  margin: 8px 0 6px;
}

.comment-box {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--aecom-lime-green);
  border-radius: 6px;
  padding: 10px;
  font-family: inherit;
  font-size: 0.95rem;
}

.btn-primary, .btn-secondary {
  display: inline-block;
  padding: 12px 28px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid var(--aecom-earth-green);
  transition: all 0.3s ease;
  text-decoration: none;
  margin-top: 12px;
}
.btn-primary { background: var(--aecom-earth-green); color: var(--aecom-white); }
.btn-primary:hover:not(:disabled) { background: var(--aecom-midnight-teal); border-color: var(--aecom-midnight-teal); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: transparent; color: var(--aecom-midnight-teal); border-color: var(--aecom-midnight-teal); }
.btn-secondary:hover { background: var(--aecom-midnight-teal); color: var(--aecom-white); }

.notice-info {
  background: #f0f7f5;
  border-left: 4px solid var(--aecom-earth-green);
  padding: 12px 16px;
  border-radius: 4px;
  color: var(--aecom-midnight-teal);
}
.notice-warn {
  background: #fff3f3;
  border-left: 4px solid var(--aecom-coral-orange, #e52713);
  padding: 12px 16px;
  border-radius: 4px;
  color: #b3200f;
}
.review-loading { color: #666; font-style: italic; padding: 20px; }

:deep(.attr-def) {
  display: block;
  font-weight: 400;
  font-size: 0.82rem;
  color: var(--aecom-black-75, #646363);
}
:deep(.survey-intro), :deep(.notice-info), :deep(.licence-box) {
  padding: 12px 16px;
  border-radius: 4px;
}
</style>
